// AIMapGeneratorModal - AI でピースマップを生成（条件選択フォーム形式）
// 旧 AIChatModal（チャット形式）を置換。左: 条件フォーム / 右: 3案プレビュー。
// チャットUIは廃すが、繰り返し生成時は session_id/response_id で前後コンテキストを引き継ぐ
// （初回 aiGenerate → 2回目以降 aiRefine、30日失効 410 は aiGenerate へフォールバック）。
// トップ画面と同じ専用ダイアログ（InScreenModal + BuilderModalPanel）で表示する。

import { useState } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { Rating } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import { RootState } from "app/store";
import I18n from "common/i18n";
import { usePlaySound, btnDelay } from "common/utils";
import { actions } from "features/builder/slice";
import { HELP_AI } from "features/builder/helpPages";
import { aiGenerate, aiRefine } from "features/builder/api";
import { MapMiniPreview } from "features/builder/canvas/MapMiniPreview";
import { useBuilderContext } from "features/builder/context/BuilderContext";
import { COMMAND_OPTIONS, COMMAND_CATEGORIES } from "features/builder/commandOptions";
import {
  Difficulty,
  PieceMilestone,
  BuilderCommandId,
  DEFAULT_ENABLED_COMMANDS,
  GeneratedMapData,
} from "features/builder/types";
import {
  PillSize,
  PillColor,
  InScreenModal,
  BuilderModalPanel,
  BuilderPillButton,
  BuilderModalAccent,
} from "features/builder/elements";
import { BuilderHelpPopup } from "./BuilderHelpPopup";

export const AIMapGeneratorModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const play = usePlaySound();
  const dispatch = useDispatch();
  const { triggerReload } = useBuilderContext();

  const size = useSelector((state: RootState) => state.builder.size);
  const pieceId = useSelector((state: RootState) => state.builder.editingBuilderMapId);
  const active = useSelector((state: RootState) => state.user.active);

  const [difficulty, setDifficulty] = useState<Difficulty>(Difficulty.MEDIUM);
  const [floors, setFloors] = useState(1);
  const [selectedCommands, setSelectedBlocks] = useState<Set<BuilderCommandId>>(
    new Set(DEFAULT_ENABLED_COMMANDS),
  );
  const [message, setMessage] = useState("");
  const [variations, setVariations] = useState<GeneratedMapData[]>([]);
  // ライブ表示（回せる）にするのは 1 案だけ。残りは静止画
  const [activeIndex, setActiveIndex] = useState(0);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const handleClose = () => {
    if (isGenerating) return;
    play();
    btnDelay(() => onClose());
  };

  const handleToggleCommand = (commandId: BuilderCommandId) => {
    play();
    setSelectedBlocks((prev) => {
      const next = new Set(prev);
      if (next.has(commandId)) {
        next.delete(commandId);
      } else {
        next.add(commandId);
      }
      return next;
    });
  };

  const runGenerate = async () => {
    setIsGenerating(true);
    const enabledCommands = Array.from(selectedCommands);
    const prompt = message.trim();
    try {
      let result;
      if (sessionId) {
        try {
          result = await aiRefine({
            uid: active.uid,
            session_id: sessionId,
            prompt,
            difficulty,
            size: { x: size.x, z: size.z },
            enabled_commands: enabledCommands,
            floors,
          });
        } catch (err) {
          // 410 Gone: session 失効 → 新規 session に自動フォールバック
          if ((err as { status?: number }).status === 410) {
            toast.warn(
              <div>
                <p className="text text-textcolor-black">
                  {I18n.t("MSG_BUILDER_AI_SESSION_EXPIRED")}
                </p>
              </div>,
            );
            result = await aiGenerate({
              uid: active.uid,
              piece_id: pieceId,
              prompt,
              difficulty,
              size: { x: size.x, z: size.z },
              enabled_commands: enabledCommands,
              floors,
            });
          } else {
            throw err;
          }
        }
      } else {
        result = await aiGenerate({
          uid: active.uid,
          piece_id: pieceId,
          prompt,
          difficulty,
          size: { x: size.x, z: size.z },
          enabled_commands: enabledCommands,
          floors,
        });
      }
      setSessionId(result.session_id);
      setVariations(result.variations);
      setActiveIndex(0);
    } catch (err) {
      console.error("AI generate failed:", err);
      toast.warn(
        <div>
          <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_AI_GENERATION_FAILED")}</p>
        </div>,
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerate = () => {
    if (isGenerating) return;
    play();
    btnDelay(() => runGenerate());
  };

  const handleReset = () => {
    if (isGenerating) return;
    play();
    btnDelay(() => {
      setSessionId(null);
      setVariations([]);
    });
  };

  const handleUseVariation = (variation: GeneratedMapData) => () => {
    play();
    btnDelay(() => {
      dispatch(actions.applyGeneratedMap(variation));
      // AI でつくったのも区切り。画面は 3D を作り直してから撮る
      dispatch(actions.requestMilestone({ action_type: PieceMilestone.AI_GENERATE }));
      triggerReload();
      onClose();
    });
  };

  const difficultyOptions = [
    { value: Difficulty.EASY, labelKey: "MSG_BUILDER_DIFFICULTY_EASY", level: 1 },
    { value: Difficulty.MEDIUM, labelKey: "MSG_BUILDER_DIFFICULTY_MEDIUM", level: 2 },
    { value: Difficulty.HARD, labelKey: "MSG_BUILDER_DIFFICULTY_HARD", level: 3 },
  ];

  return (
    <InScreenModal open={open}>
      <BuilderModalPanel
        accent={BuilderModalAccent.BLUE}
        icon="smart_toy"
        title={I18n.t("MSG_BUILDER_AI_MAP_TITLE")}
        width={1000}
        onClose={handleClose}
        onHelp={() => setShowHelp(true)}
      >
        {/* ボディ：左 条件フォーム / 右 プレビュー */}
        <div className="flex-row-el w-full h-[440px] min-h-0 rounded-xl border-solid border-2 border-gray2-60 overflow-hidden">
          {/* 左: 条件フォーム + 生成ボタン */}
          <div className="flex-col-el w-[440px] shrink-0 min-h-0 border-r-2 border-gray2-60">
            <div className="flex-col-el items-stretch flex-1 min-h-0 w-full gap-4 p-4 overflow-y-auto invisible-scrollbar">
              {/* むずかしさ。3つから選ぶ。星はアプリ評価と同じ Rating をそのまま使う */}
              <div data-guide="ai_level" className="flex-col-el items-stretch w-full gap-2">
                <p className="type-text-left type-md text-textcolor-black w-full">
                  {I18n.t("MSG_BUILDER_DIFFICULTY_LABEL")}
                </p>
                <div className="flex-row-el w-full gap-2">
                  {difficultyOptions.map((opt) => {
                    const isSelected = difficulty === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => {
                          play();
                          setDifficulty(opt.value);
                        }}
                        className={`type-inherit-font flex-col-el flex-center flex-1 min-w-0 gap-1 px-2 py-3 rounded-xl border-solid border-2 cursor-pointer transition-all active:scale-95 ${
                          isSelected ? "bg-blue-10 border-blue-100" : "bg-white border-gray2-60"
                        }`}
                      >
                        <Rating
                          readOnly
                          max={3}
                          value={opt.level}
                          sx={{ fontSize: "28px" }}
                          emptyIcon={<StarIcon fontSize="inherit" className="text-gray2-30" />}
                        />
                        <p
                          className={`type-text type-sm ${
                            isSelected ? "text-blue-100" : "text-textcolor-black/50"
                          }`}
                        >
                          {I18n.t(opt.labelKey)}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* たかさ（なんかい） */}
              <div data-guide="ai_floor" className="flex-col-el items-stretch w-full gap-2">
                <p className="type-text-left type-md text-textcolor-black w-full">
                  {I18n.t("MSG_BUILDER_AI_FLOORS_LABEL")}
                </p>
                <div className="flex-row-el w-full gap-2">
                  {[1, 2, 3].map((n) => {
                    const isSelected = floors === n;
                    return (
                      <button
                        key={n}
                        type="button"
                        onClick={() => {
                          play();
                          setFloors(n);
                        }}
                        className={`type-inherit-font flex-col-el flex-center flex-1 min-w-0 gap-1 py-3 rounded-xl border-solid border-2 cursor-pointer transition-all active:scale-95 ${
                          isSelected ? "bg-blue-10 border-blue-100" : "bg-white border-gray2-60"
                        }`}
                      >
                        <span
                          className={`type-text type-title ${
                            isSelected ? "text-blue-100" : "text-textcolor-black/50"
                          }`}
                        >
                          {n}
                          <span className="type-sm">{I18n.t("MSG_BUILDER_AI_FLOOR_UNIT")}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* つかうコマンド */}
              <div className="flex-col-el items-stretch w-full gap-2">
                <p className="type-text-left type-md text-textcolor-black w-full">
                  {I18n.t("MSG_BUILDER_AI_COMMANDS_LABEL")}
                </p>
                {COMMAND_CATEGORIES.map((category) => (
                  <div key={category.id} className="flex-col-el items-start w-full gap-1">
                    <p className="type-text-left type-sm text-textcolor-black/60 w-full">
                      {I18n.t(category.labelKey)}
                    </p>
                    <div className="flex-col-el w-full gap-1">
                      {COMMAND_OPTIONS.filter((b) => b.category === category.id).map((block) => {
                        const isSelected = selectedCommands.has(block.id);
                        return (
                          <button
                            key={block.id}
                            type="button"
                            onClick={() => handleToggleCommand(block.id)}
                            className={`type-inherit-font flex-row-el items-center w-full gap-3 px-4 py-2 rounded-xl border-solid border-2 cursor-pointer transition-all active:scale-95 ${
                              isSelected
                                ? `${category.bgClass} ${category.borderClass}`
                                : "bg-white border-gray2-60"
                            }`}
                          >
                            <span
                              className={`material-symbols-outlined type-md ${
                                isSelected
                                  ? `material-symbols-filled ${category.accentClass}`
                                  : "text-gray2-100"
                              }`}
                            >
                              {isSelected ? "check_box" : "check_box_outline_blank"}
                            </span>
                            {/* コマンドのアイコン。他の画面と同じ絵・同じ色にする */}
                            <span
                              className={`material-symbols-outlined type-md ${
                                isSelected ? category.accentClass : "text-gray2-100"
                              }`}
                            >
                              {block.icon}
                            </span>
                            <p
                              className={`type-text-left type-sm flex-1 ${
                                isSelected ? "text-textcolor-black" : "text-textcolor-black/60"
                              }`}
                            >
                              {I18n.t(block.labelKey)}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* ついかメッセージ（任意） */}
              <div className="flex-col-el items-stretch w-full gap-2">
                <p className="type-text-left type-md text-textcolor-black w-full">
                  {I18n.t("MSG_BUILDER_AI_PROMPT_LABEL")}
                </p>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={I18n.t("MSG_BUILDER_AI_PROMPT_PLACEHOLDER")}
                  maxLength={100}
                  rows={2}
                  className="input-modal type-sm text-textcolor-black w-full p-3 resize-none"
                />
              </div>
            </div>

            {/* 生成ボタン（＋ session があれば さいしょから） */}
            <div
              data-guide="ai_create"
              className="flex-row-el items-center w-full gap-4 p-4 pb-6 border-t-2 border-gray2-60"
            >
              {sessionId && (
                <BuilderPillButton
                  color={PillColor.BLUE}
                  size={PillSize.MD}
                  icon="restart_alt"
                  label={I18n.t("MSG_BUILDER_AI_RESET")}
                  disabled={isGenerating}
                  onClick={handleReset}
                />
              )}
              <BuilderPillButton
                color={PillColor.GREEN}
                size={PillSize.MD}
                icon="auto_awesome"
                label={I18n.t("MSG_AI_MATERIAL_GENERATE_BUTTON")}
                disabled={isGenerating}
                className="!flex-1"
                onClick={handleGenerate}
              />
            </div>
          </div>

          {/* 右: 3案プレビュー */}
          <div className="flex-col-el items-stretch flex-1 min-w-0 min-h-0 gap-3 p-4 overflow-y-auto invisible-scrollbar">
            {isGenerating ? (
              <div className="flex-col-el flex-center flex-1 w-full gap-2">
                <div className="w-10 h-10 border-solid border-4 border-blue-100 border-t-transparent rounded-full animate-spin" />
                <span className="type-text type-sm text-textcolor-black/50">
                  {I18n.t("MSG_BUILDER_AI_GENERATING")}
                </span>
              </div>
            ) : variations.length === 0 ? (
              <div className="flex-col-el flex-center flex-1 w-full">
                <span className="type-text type-sm text-textcolor-black/40">
                  {I18n.t("MSG_BUILDER_AI_GREETING")}
                </span>
              </div>
            ) : (
              <>
                {variations.map((variation, i) => (
                  <div
                    key={i}
                    className="flex-col-el items-stretch w-full gap-2 rounded-xl border-solid border-2 border-gray2-60 p-3"
                  >
                    <span className="type-text-left type-sm text-textcolor-black">
                      {I18n.t("MSG_BUILDER_AI_PLAN_LABEL", { n: i + 1 })}
                    </span>
                    <div className="flex-col-el w-full rounded-lg bg-beige-60 p-2">
                      <MapMiniPreview
                        data={variation}
                        size={{ x: size.x, z: size.z }}
                        active={i === activeIndex}
                        onActivate={() => setActiveIndex(i)}
                      />
                    </div>
                    <div className="flex-row-el w-full pb-1">
                      <BuilderPillButton
                        color={PillColor.GREEN}
                        size={PillSize.SM}
                        icon="check"
                        label={I18n.t("MSG_BUILDER_AI_USE_THIS")}
                        className="!flex-1"
                        onClick={handleUseVariation(variation)}
                      />
                    </div>
                  </div>
                ))}

                {/* もう一度 3案（refine で再生成） */}
                <div className="flex-row-el w-full pb-1">
                  <BuilderPillButton
                    color={PillColor.WHITE_BLUE}
                    size={PillSize.SM}
                    icon="refresh"
                    label={I18n.t("MSG_BUILDER_AI_REGENERATE")}
                    disabled={isGenerating}
                    className="!flex-1"
                    onClick={handleGenerate}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </BuilderModalPanel>

      <BuilderHelpPopup open={showHelp} onClose={() => setShowHelp(false)} chapters={HELP_AI} />
    </InScreenModal>
  );
};
